"use client";

import { icons } from "@/components/ui/icons";
import { cn } from "@/lib/ui/cn";

function generatePageNumbers(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | "ellipsis")[] = [];

  if (currentPage <= 4) {
    for (let i = 1; i <= 4; i++) {
      pages.push(i);
    }
    pages.push("ellipsis");
    pages.push(totalPages);
  } else if (currentPage >= totalPages - 3) {
    pages.push(1);
    pages.push("ellipsis");
    for (let i = totalPages - 3; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    pages.push("ellipsis");
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push("ellipsis");
    pages.push(totalPages);
  }

  return pages;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onScroll?: () => void;
  className?: string;
  rootClassName?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  onScroll,
  className,
  rootClassName,
}: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  const handlePageChange = (page: number) => {
    onPageChange(page);
    onScroll?.();
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const ArrowBackIcon = icons.ArrowBack;
  const ArrowForwardIcon = icons.ArrowForward;

  return (
    <div className={cn("flex w-full items-center justify-center", rootClassName)}>
      <div
        className={cn(
          "tablet:gap-12 tablet:max-w-[1400px] tablet:justify-center flex w-full max-w-[393px] items-center justify-between gap-2",
          className,
        )}
      >
        {/* Previous Button */}
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={cn(
            "tablet:h-12 tablet:w-12 flex h-8 w-8 items-center justify-center gap-2 rounded-full p-3 transition-opacity",
            currentPage === 1
              ? "cursor-not-allowed bg-white/60 opacity-60"
              : "cursor-pointer bg-white/60 hover:opacity-80",
          )}
          aria-label="Previous page"
        >
          <div className="relative h-6 w-6 shrink-0 overflow-hidden">
            <ArrowBackIcon className="text-neutral-darkest absolute top-[4.19px] left-[4.19px] h-4 w-4" />
          </div>
        </button>

        {/* Page Numbers */}
        <div className="gap-gap-element-lg flex items-center justify-start">
          {pageNumbers.map((page, index) => {
            if (page === "ellipsis") {
              return (
                <div
                  key={`ellipsis-${index}`}
                  className="font-header justify-start text-xl leading-8 font-medium text-white/60"
                >
                  ...
                </div>
              );
            }

            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={cn(
                  "px-padding-element-lg py-padding-element-2xs tablet:h-16 tablet:w-16 flex h-8 w-8 items-center justify-center gap-2 rounded-md outline-2 transition-[opacity,colors] hover:opacity-80",
                  isActive ? "bg-white/10 outline-white/40" : "outline-neutral bg-transparent",
                )}
                aria-label={`Go to page ${page}`}
                aria-current={isActive ? "page" : undefined}
              >
                <div
                  className={cn(
                    "typography-text-regular-normal tablet:typography-text-medium-medium justify-start",
                    isActive ? "text-white/40" : "text-white",
                  )}
                >
                  {page}
                </div>
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={cn(
            "tablet:h-12 tablet:w-12 flex h-8 w-8 items-center justify-center gap-2 rounded-full p-3 transition-opacity",
            currentPage === totalPages
              ? "cursor-not-allowed bg-white opacity-60"
              : "cursor-pointer bg-white hover:opacity-80",
          )}
          aria-label="Next page"
        >
          <div className="relative h-6 w-6 shrink-0 overflow-hidden">
            <ArrowForwardIcon className="text-neutral-darkest absolute top-[4.19px] left-[3.85px] h-4 w-4" />
          </div>
        </button>
      </div>
    </div>
  );
}
